import { doc, getDoc, onSnapshot, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Per-location ordering kill switch.
 *
 * Stored in a single Firestore doc (settings/ordering) so an admin can pause
 * new online orders for one warehouse — trucks down, staff out, inventory
 * being moved — without touching products, prices, or the calendar. Flipping
 * it back on restores ordering instantly; nothing else about the location
 * changes while it's paused.
 *
 * Shape:
 *   {
 *     Angola:  { paused: bool, message: string, updatedAt: Timestamp, updatedBy: string },
 *     Elkhart: { ... }
 *   }
 */

const SETTINGS_COLLECTION = 'settings';
const ORDERING_DOC_ID = 'ordering';

// The two service areas we run. Kept here (rather than derived from the
// locations collection) because these keys must match the values stored on
// products — "Angola" / "Elkhart" / "Both".
export const ORDERING_LOCATIONS = ['Angola', 'Elkhart'];

export const DEFAULT_PAUSE_MESSAGE =
  "We've temporarily stopped taking new online orders for this location. " +
  'Please call us at (260) 226-5640 — we may still be able to help.';

const orderingDocRef = () => doc(db, SETTINGS_COLLECTION, ORDERING_DOC_ID);

// Everything defaults to "open for business" so a missing doc, a read failure,
// or a brand-new environment never accidentally blocks orders.
const emptyStatus = () =>
  ORDERING_LOCATIONS.reduce((acc, name) => {
    acc[name] = { paused: false, message: '', updatedAt: null, updatedBy: '' };
    return acc;
  }, {});

/**
 * Match a free-form location value ("angola", "Angola", "Both") to a known
 * service area. Returns null for "Both" / add-ons / unknown values, which are
 * not tied to a single warehouse.
 */
export const normalizeLocationKey = (location) => {
  if (!location) return null;
  const value = String(location).trim().toLowerCase();
  return ORDERING_LOCATIONS.find((name) => name.toLowerCase() === value) || null;
};

const normalizeStatus = (data) => {
  const status = emptyStatus();
  if (!data) return status;

  ORDERING_LOCATIONS.forEach((name) => {
    // Tolerate lower/upper-case keys written by scripts or the console.
    const entry =
      data[name] ||
      data[name.toLowerCase()] ||
      data[name.toUpperCase()] ||
      null;
    if (!entry) return;

    status[name] = {
      paused: entry.paused === true,
      message: entry.message || '',
      updatedAt: entry.updatedAt?.toDate ? entry.updatedAt.toDate() : entry.updatedAt || null,
      updatedBy: entry.updatedBy || ''
    };
  });

  return status;
};

/** One-shot read. Falls back to "open" if the doc or the read fails. */
export const getOrderingStatus = async () => {
  try {
    const snapshot = await getDoc(orderingDocRef());
    return normalizeStatus(snapshot.exists() ? snapshot.data() : null);
  } catch (error) {
    console.error('Error fetching ordering status:', error);
    return emptyStatus();
  }
};

/**
 * Live subscription. Used by the storefront so a pause takes effect on open
 * browser tabs without a refresh. Returns an unsubscribe function.
 */
export const subscribeToOrderingStatus = (onChange, onError) => {
  return onSnapshot(
    orderingDocRef(),
    (snapshot) => {
      onChange(normalizeStatus(snapshot.exists() ? snapshot.data() : null));
    },
    (error) => {
      console.error('Error subscribing to ordering status:', error);
      // Fail open — a listener error must never take the store offline.
      onChange(emptyStatus());
      if (onError) onError(error);
    }
  );
};

/**
 * Pause or resume online ordering for one location.
 * @param {string} location - 'Angola' | 'Elkhart'
 * @param {boolean} paused
 * @param {{ message?: string, updatedBy?: string }} [options]
 */
export const setLocationOrderingPaused = async (location, paused, options = {}) => {
  const key = normalizeLocationKey(location);
  if (!key) {
    throw new Error(`Unknown location: ${location}`);
  }

  const entry = {
    paused: !!paused,
    message: paused ? (options.message || DEFAULT_PAUSE_MESSAGE) : '',
    updatedAt: serverTimestamp(),
    updatedBy: options.updatedBy || ''
  };

  try {
    // merge so the other location's settings are left untouched, and so the
    // doc is created on first use without a separate bootstrap step.
    await setDoc(orderingDocRef(), { [key]: entry }, { merge: true });
    return { ...entry, location: key, updatedAt: new Date() };
  } catch (error) {
    console.error('Error updating ordering status:', error);
    throw new Error(`Failed to update ordering status for ${key}`);
  }
};

/**
 * Is ordering paused for the warehouse that would fulfill this item?
 * Values that aren't tied to one warehouse ("Both", add-ons, blank) are only
 * blocked when every location is paused.
 */
export const isOrderingPausedFor = (status, location) => {
  if (!status) return false;

  const key = normalizeLocationKey(location);
  if (key) return status[key]?.paused === true;

  return ORDERING_LOCATIONS.every((name) => status[name]?.paused === true);
};

/** The customer-facing message for a paused location (empty when open). */
export const getPauseMessageFor = (status, location) => {
  if (!isOrderingPausedFor(status, location)) return '';

  const key = normalizeLocationKey(location);
  if (key) return status[key]?.message || DEFAULT_PAUSE_MESSAGE;

  // "Both"-type item with every location paused — use whichever message is set.
  const firstMessage = ORDERING_LOCATIONS.map((name) => status[name]?.message).find(Boolean);
  return firstMessage || DEFAULT_PAUSE_MESSAGE;
};
